// CallWhisperer.jsx -- Floating in-call overlay
// ASCII only. var declarations. ESM only. Inline styles only.
import { useState, useEffect, useRef } from 'react';
import { useTheme, FONT } from '../lib/theme.js';
import AgentAvatar from './AgentAvatar.jsx';

export default function CallWhisperer(props) {
  var t = useTheme();
  var active = props.active;
  var callIdProp = props.callId;
  var transcript = props.transcript || '';
  var _autoCallId = useState(null); var autoCallId = _autoCallId[0]; var setAutoCallId = _autoCallId[1];
  var _noActiveCall = useState(false); var noActiveCall = _noActiveCall[0]; var setNoActiveCall = _noActiveCall[1];
  var _prep = useState(null); var prep = _prep[0]; var setPrep = _prep[1];
  var _prepTimeout = useState(false); var prepTimeout = _prepTimeout[0]; var setPrepTimeout = _prepTimeout[1];
  var _suggestions = useState([]); var suggestions = _suggestions[0]; var setSuggestions = _suggestions[1];
  var _fadeMap = useState({}); var fadeMap = _fadeMap[0]; var setFadeMap = _fadeMap[1];
  var _cloneSuggestion = useState(null); var cloneSuggestion = _cloneSuggestion[0]; var setCloneSuggestion = _cloneSuggestion[1];
  var _liveTranscript = useState(''); var liveTranscript = _liveTranscript[0]; var setLiveTranscript = _liveTranscript[1];
  var _qaInput = useState(''); var qaInput = _qaInput[0]; var setQaInput = _qaInput[1];
  var _qaAnswer = useState(''); var qaAnswer = _qaAnswer[0]; var setQaAnswer = _qaAnswer[1];
  var _qaAgent = useState(''); var qaAgent = _qaAgent[0]; var setQaAgent = _qaAgent[1];
  var pollRef = useRef(null);
  var transcriptPollRef = useRef(null);
  var activeDetectRef = useRef(null);
  var prepTimeoutRef = useRef(null);
  var lastTranscriptRef = useRef('');

  // Resolve the active callId: prop takes priority, then auto-detected
  var callId = callIdProp || autoCallId;

  // Auto-detect active call when no callId prop is provided
  useEffect(function() {
    if (!active || callIdProp) {
      if (activeDetectRef.current) clearInterval(activeDetectRef.current);
      setAutoCallId(null);
      setNoActiveCall(false);
      return;
    }

    function detectActiveCall() {
      fetch('/api/calls/latest-active')
        .then(function(r) { return r.json(); })
        .then(function(d) {
          if (d.ok && d.data && d.data.id) {
            setAutoCallId(d.data.id);
            setNoActiveCall(false);
            clearInterval(activeDetectRef.current);
          } else {
            setNoActiveCall(true);
          }
        })
        .catch(function() { setNoActiveCall(true); });
    }

    detectActiveCall();
    activeDetectRef.current = setInterval(detectActiveCall, 5000);
    return function() { if (activeDetectRef.current) clearInterval(activeDetectRef.current); };
  }, [active, callIdProp]);

  // Fetch prep brief when callId is known
  useEffect(function() {
    if (!active || !callId) return;
    setPrep(null);
    setPrepTimeout(false);
    if (prepTimeoutRef.current) clearTimeout(prepTimeoutRef.current);

    fetch('/api/calls/' + callId + '/brief', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
      .then(function(r) { return r.json(); })
      .then(function(d) {
        if (d.ok) setPrep(d.data);
        else setPrepTimeout(true);
      })
      .catch(function() { setPrepTimeout(true); });

    // After 3s with no prep, show transcript area anyway
    prepTimeoutRef.current = setTimeout(function() {
      setPrepTimeout(true);
    }, 3000);

    return function() { if (prepTimeoutRef.current) clearTimeout(prepTimeoutRef.current); };
  }, [active, callId]);

  // Poll for live suggestions every 15s using latest transcript chunk
  useEffect(function() {
    if (!active) {
      if (pollRef.current) clearInterval(pollRef.current);
      setSuggestions([]);
      return;
    }

    function fetchSuggestions() {
      var chunk = lastTranscriptRef.current;
      if (!chunk) return;
      fetch('/api/calls/live-context', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transcript: chunk })
      })
        .then(function(r) { return r.json(); })
        .then(function(d) {
          if (d.ok && d.data && d.data.suggestions) {
            var incoming = d.data.suggestions;
            var newFade = {};
            for (var i = 0; i < incoming.length; i++) { newFade[i] = 'in'; }
            setSuggestions(incoming);
            setFadeMap(newFade);
          }
        })
        .catch(function() {});
    }

    fetchSuggestions();
    pollRef.current = setInterval(fetchSuggestions, 15000);
    return function() { if (pollRef.current) clearInterval(pollRef.current); };
  }, [active]);

  // Fetch clone suggestion
  useEffect(function() {
    if (!active) return;
    fetch('/api/clone/' + encodeURIComponent(props.userEmail || 'aaron@sprintmode.co') + '/progress')
      .then(function(r) { return r.json(); })
      .then(function(d) {
        if (d.ok && d.data && d.data.profile && d.data.profile.operating_manual) {
          setCloneSuggestion('Your clone suggests: Lead with ' + d.data.profile.priorities.split('.')[0]);
        }
      })
      .catch(function() {});
  }, [active]);

  // Poll live transcript from D1 every 5 seconds while active callId is known
  useEffect(function() {
    if (!active || !callId) {
      if (transcriptPollRef.current) clearInterval(transcriptPollRef.current);
      setLiveTranscript('');
      return;
    }

    function fetchTranscript() {
      fetch('/api/calls/' + callId)
        .then(function(r) { return r.json(); })
        .then(function(d) {
          if (d.ok && d.data && d.data.call && d.data.call.transcript) {
            var full = d.data.call.transcript || '';
            var chunk = full.length > 500 ? full.slice(-500) : full;
            setLiveTranscript(chunk);
            lastTranscriptRef.current = chunk;
            if (props.onTranscript) props.onTranscript(chunk);
          }
        })
        .catch(function() {});
    }

    fetchTranscript();
    transcriptPollRef.current = setInterval(fetchTranscript, 5000);
    return function() { if (transcriptPollRef.current) clearInterval(transcriptPollRef.current); };
  }, [active, callId]);

  // Keep ref in sync with transcript prop (for manual/pre-existing transcript)
  useEffect(function() {
    if (transcript) lastTranscriptRef.current = transcript;
  }, [transcript]);

  function handleAsk() {
    if (!qaInput.trim() || !callId) return;
    var q = qaInput.trim();
    setQaInput('');
    setQaAnswer('Thinking...');
    setQaAgent('');
    fetch('/api/calls/' + callId + '/ask', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ question: q })
    })
    .then(function(r) { return r.json(); })
    .then(function(d) {
      if (d.ok && d.data) {
        setQaAnswer(d.data.answer || 'No answer');
        setQaAgent(d.data.agent || 'Hub');
      } else {
        setQaAnswer('Error: ' + (d.error || 'Unknown'));
      }
    })
    .catch(function(e) { setQaAnswer('Error: ' + e.message); });
  }

  if (!active) return null;

  // Determine what the prep area shows
  var showPrep = prep !== null;
  var showPrepLoading = !prep && !prepTimeout && callId;
  var showTranscriptArea = prepTimeout || liveTranscript;

  return (
    <div style={{ position: 'fixed', top: 60, right: 16, width: 300, maxHeight: '80vh', overflowY: 'auto', background: t.surface, border: '1px solid ' + t.border, borderRadius: 14, boxShadow: '0 8px 32px rgba(0,0,0,0.12)', zIndex: 200, padding: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <AgentAvatar agentId="agent_call" size={24} />
        <span style={{ fontSize: 13, fontWeight: 700, fontFamily: FONT }}>Call Whisperer</span>
        <button onClick={props.onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: t.textMuted, fontSize: 16 }}>x</button>
      </div>

      {/* No active call state */}
      {!callId && noActiveCall && (
        <div style={{ fontSize: 12, color: t.textMuted, fontFamily: FONT, padding: '8px 0' }}>
          <div>No active call. Start recording from the Chrome extension.</div>
          <div style={{ marginTop: 6, fontSize: 11, color: t.accent }}>For the best experience, use the Hub Whisperer side panel in Chrome.</div>
        </div>
      )}

      {/* Waiting to detect call */}
      {!callId && !noActiveCall && (
        <div style={{ fontSize: 12, color: t.textMuted, fontFamily: FONT, padding: '8px 0' }}>
          Listening for active call...
        </div>
      )}

      {/* Active call detected badge */}
      {callId && !callIdProp && (
        <div style={{ fontSize: 11, color: t.textMuted, fontFamily: FONT, marginBottom: 8, background: '#10B98110', border: '1px solid #10B98130', borderRadius: 6, padding: '4px 8px' }}>
          Auto-detected call
        </div>
      )}

      {suggestions.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, fontFamily: FONT, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Live Context</div>
          {suggestions.map(function(s, idx) {
            var bg = s.type === 'finance' ? '#4F6EF710' : '#10B98110';
            var border = s.type === 'finance' ? '#4F6EF730' : '#10B98130';
            var label = s.type === 'finance' ? 'Finance' : (s.name || 'Client');
            return (
              <div key={idx} style={{ background: bg, border: '1px solid ' + border, borderRadius: 8, padding: '8px 10px', marginBottom: 6, opacity: fadeMap[idx] === 'in' ? 1 : 0.85, transition: 'opacity 0.4s' }}>
                <div style={{ fontSize: 11, fontWeight: 700, fontFamily: FONT, color: t.text, marginBottom: 2 }}>{label}</div>
                <div style={{ fontSize: 11, fontFamily: FONT, color: t.textMuted }}>{s.detail}</div>
              </div>
            );
          })}
        </div>
      )}

      {showTranscriptArea && liveTranscript && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, fontFamily: FONT, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Live Transcript</div>
          <div style={{ background: '#1A1D2310', border: '1px solid #1A1D2318', borderRadius: 8, padding: '8px 10px', maxHeight: 120, overflowY: 'auto' }}>
            <span style={{ fontSize: 11, fontFamily: FONT, color: t.text, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{liveTranscript}</span>
          </div>
        </div>
      )}

      {showPrepLoading && (
        <div style={{ fontSize: 12, color: t.textMuted, fontFamily: FONT }}>Loading prep...</div>
      )}

      {showPrep && (
        <div style={{ fontSize: 12, lineHeight: 1.6, color: t.text, fontFamily: FONT, whiteSpace: 'pre-wrap' }}>
          {typeof prep === 'string' ? prep : (prep.brief || prep.answer || prep.body || JSON.stringify(prep))}
        </div>
      )}

      {prepTimeout && !liveTranscript && !callId && (
        <div style={{ fontSize: 12, color: t.textMuted, fontFamily: FONT }}>No active call. Start recording from the Chrome extension.</div>
      )}

      {cloneSuggestion && (
        <div style={{ marginTop: 12, background: '#7C3AED10', border: '1px solid #7C3AED30', borderRadius: 8, padding: '8px 10px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, fontFamily: FONT, color: '#7C3AED', marginBottom: 2 }}>Clone</div>
          <div style={{ fontSize: 11, fontFamily: FONT, color: t.textMuted }}>{cloneSuggestion}</div>
        </div>
      )}

      {/* Q&A input */}
      {callId && (
        <div style={{ marginTop: 12, borderTop: '1px solid ' + t.border, paddingTop: 10 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              value={qaInput}
              onChange={function(e) { setQaInput(e.target.value); }}
              onKeyDown={function(e) { if (e.key === 'Enter') handleAsk(); }}
              placeholder="Ask during the call..."
              style={{
                flex: 1, background: t.surfaceInset, border: '1px solid ' + t.border,
                borderRadius: 6, padding: '6px 8px', color: t.text, fontSize: 11,
                outline: 'none', fontFamily: FONT
              }}
            />
            <button onClick={handleAsk} style={{
              background: t.accent, border: 'none', borderRadius: 6,
              padding: '6px 12px', color: '#fff', fontSize: 11, fontWeight: 600,
              cursor: 'pointer'
            }}>Ask</button>
          </div>
          {qaAnswer && (
            <div style={{
              marginTop: 6, background: '#1A224080', border: '1px solid #4F6EF720',
              borderRadius: 6, padding: '6px 8px', fontSize: 11, lineHeight: 1.5, color: t.text
            }}>
              <div style={{ fontSize: 10, color: t.accent, fontFamily: "'IBM Plex Mono', monospace", marginBottom: 2 }}>{qaAgent}</div>
              {qaAnswer}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
